import { useEffect, useMemo, useRef, useState } from "react";
import type { FrameRecords, SideDisplayConfig } from "../types/api";
import { allColumns, applyPins, cellText } from "../lib/columns";
import { useColumnFilters } from "../lib/columnFilters";
import { useColumnPins, type PinSide } from "../lib/columnPins";
import { sideSpans, sideOf, rawKeyOf } from "../lib/columnGroups";
import { useBandLabels, BAND_LABEL_ATTR } from "../lib/bandLabels";
import { useGridSelection } from "./useGridSelection";
import { useColumnWidths } from "./useColumnWidths";
import { useFrozenOffsets } from "../lib/frozenOffsets";
import { ledgerColumns, sortPositions, trackValues, type SortSpec } from "../lib/ledgerView";
import { matchGroupCellClass } from "../lib/matchGroups";
import { matchEvidenceCellClass } from "../lib/matchEvidence";
import EmptyState from "./EmptyState";
import FullscreenTable from "./FullscreenTable";
import { HeadControls, TrackChips } from "./GridControls";
import { SearchPanel, useMultiSearch } from "./SearchPanel";

interface Props {
  frame: FrameRecords;
  pageSize?: number;
  config?: SideDisplayConfig;
  // Pin scope. CRI pins are shared across insurers (one export format); insurer pins
  // are per-insurer. Omitted → pinning is disabled for this grid.
  pinSide?: PinSide;
  insurerName?: string;
  // Paired frames only: columns are composite (cri::x / ins::x), so render the
  // spanning CRI | Insurer banner above the headers instead of naming the side in
  // every column label.
  grouped?: boolean;
  // Title shown in the full-screen header bar.
  title?: string;
  // Render every curated column even when it is blank in THIS slice. For a grid whose
  // column set was already decided over a wider population (lib/columns::
  // populatedColumns) — otherwise the same table shows different columns depending on
  // which view the reader is standing in.
  keepCuratedColumns?: boolean;
  // Name a column holding a per-row MATCH BLOCK id and the grid draws each block as one
  // visual unit — a shared tint plus a left rail, with a rule where a new block starts.
  // Only for a frame whose rows are already in block order (Side by Side); on a grid
  // where a group's legs are scattered the rail would bracket unrelated rows, so those
  // grids leave it unset and rely on the Match Group column alone.
  // The column itself is never displayed (name it with a leading underscore).
  groupKeyCol?: string;
  // The Manual Match ledgers' column order (lib/ledgerView.ts::ledgerColumns): matching
  // fields first, repeated columns last, a constant status column hidden. For one book's
  // grid; finance kept the Statement tab's own order, where status leads (2026-09-28).
  keyColumnsFirst?: boolean;
  // Optional per-row control, rendered in a sticky trailing column that stays put on
  // sideways scroll (the same .sticky-note-col device the notes / auto-matched grids
  // use). Deliberately OUTSIDE the cell-selection coordinate space, which spans `vis`
  // only — a drag across the grid can never swallow a click on it or foot a button.
  // `render` returns null for a row with nothing to act on.
  rowAction?: {
    header: string;
    render: (row: unknown[]) => React.ReactNode;
  };
}

// A statement grid over the API's FrameRecords (columns + row arrays). Every column
// carries an in-header filter (searchable checklist popover of its distinct values;
// ● marks active), plus a global text search — the same interaction as the Manual
// Match grid, so every table in the app filters the same way. Filter state + distinct
// caching come from the shared useColumnFilters hook; the popover portals to body.
// Client-side global filter + pagination over the inlined rows; total_rows tells the
// user when the server capped the payload.
export default function DataTable(props: Props) {
  return (
    <FullscreenTable label={props.title || "Statement"}>
      {(scrollMax) => <DataTableBody {...props} scrollMax={scrollMax} />}
    </FullscreenTable>
  );
}

function DataTableBody({ frame, pageSize = 100, config, pinSide, insurerName = "", grouped = false,
                        rowAction, keepCuratedColumns = false, groupKeyCol, keyColumnsFirst = false,
                        scrollMax }: Props & { scrollMax?: number }) {
  const [openCol, setOpenCol] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  // Rows keep the caller's order (a statement's file order carries its running balance)
  // until the user sorts; never where rows are drawn as match blocks, which a sort would
  // split apart.
  const [sort, setSort] = useState<SortSpec | null>(null);
  const sortable = !groupKeyCol;
  const [track, setTrack] = useState<string | null>(null);
  const cf = useColumnFilters(frame);
  const pins = useColumnPins(pinSide ?? "cri", insurerName);
  const pinnable = pinSide !== undefined;

  // allColumns (not visibleColumns): curated display columns first, then EVERY other
  // populated source column. The curated list alone hid the insurer's own reference /
  // branch / UDF fields — exactly the columns needed to identify an unidentified row —
  // even though the payload carries them. Matches the drill-down tables, the Streamlit
  // original, and the export's own "show every populated column" rule.
  const vis = useMemo(() => {
    let cols = allColumns(frame, config, undefined, keepCuratedColumns);
    if (keyColumnsFirst) cols = ledgerColumns(cols, frame, frame.rows);
    return pinnable ? applyPins(cols, pins.pinned) : cols;
  }, [frame, config, pinnable, pins.pinned, keepCuratedColumns, keyColumnsFirst]);

  const search = useMultiSearch(frame, vis);
  const { text, setText, setSearchCol, setExact } = search;

  useEffect(() => {
    setText(""); setSearchCol(""); setExact(false); setOpenCol(null); setPage(0);
    setSort(null); setTrack(null);
  }, [frame, setText, setSearchCol, setExact]);

  // Frozen offsets are measured from the rendered header, capped so pinning many
  // columns can never squeeze out the scrollable region.
  const frozen = useFrozenOffsets(pinnable ? pins.pinned : [], vis);
  const lastFrozen = useMemo(() => {
    let last: string | undefined;
    for (const c of vis) if (frozen.offsetFor(c.key) !== undefined) last = c.key;
    return last;
  }, [vis, frozen]);

  // Side banner spans, derived from the FINAL column order so pinning (which can
  // interleave sides) still yields truthful banners rather than two wrong ones.
  const spans = useMemo(
    () => (grouped ? sideSpans(vis, insurerName) : []),
    [grouped, vis, insurerName],
  );
  // First column of each run gets the seam rule — but never the leftmost column,
  // which has the grid border already.
  const seamKeys = useMemo(() => {
    const s = new Set<string>();
    spans.forEach((sp, i) => { if (i > 0) s.add(sp.firstKey); });
    return s;
  }, [spans]);

  // Band labels slide to the centre of each band's visible portion as the grid
  // scrolls sideways, so a wide band never shows a nameless coloured bar.
  const bandLabels = useBandLabels(grouped, spans);

  // The column-header row sits below the banner, so its sticky `top` is the banner's
  // real height — measured, because font size and browser zoom both move it.
  const bannerRef = useRef<HTMLTableRowElement | null>(null);
  const [groupH, setGroupH] = useState(26);
  useEffect(() => {
    if (!grouped) return;
    const measure = () => {
      const h = bannerRef.current?.getBoundingClientRect().height;
      if (h) setGroupH((prev) => (Math.abs(prev - h) > 0.5 ? h : prev));
    };
    const raf = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", measure); };
  }, [grouped, spans]);

  // Track chips on a book that holds more than one track. A paired grid's keys are
  // composite (cri::track), so it never matches here.
  const trackIdx = frame.columns.indexOf("track");
  const tracks = useMemo(() => (trackIdx >= 0 ? trackValues(frame) : []), [trackIdx, frame]);

  // Filter to row POSITIONS as well as rows, so each surviving row keeps a link back to
  // its frame.indices entry — the stable id the selection's stale-guard keys on. Built
  // in the SAME pass as the row list (not a second reference-matching walk over the
  // frame, which cost ~7ms per render on a 69k-row Dubai frame).
  const { narrowed, narrowedIds, trackCounts } = useMemo(() => {
    const rows: FrameRecords["rows"] = [];
    const ids: (string | number)[] = [];
    // Per track, over everything else that narrows the rows, so each chip's count is
    // what clicking it would show.
    const counts = new Map<string, number>();
    const keep = cf.rowPredicate(frame.columns);
    // Matches the DISPLAYED text of the visible columns (SearchPanel.tsx).
    const test = search.rowTest;
    for (let p = 0; p < frame.rows.length; p++) {
      const r = frame.rows[p];
      if (keep && !keep(r)) continue;
      if (test && !test(p, r)) continue;
      if (tracks.length > 1) {
        const tv = String(r[trackIdx] ?? "");
        counts.set(tv, (counts.get(tv) ?? 0) + 1);
        if (track !== null && tv !== track) continue;
      }
      rows.push(r);
      ids.push(frame.indices[p] ?? p);
    }
    return { narrowed: rows, narrowedIds: ids, trackCounts: counts };
  }, [frame, cf, search.rowTest, tracks, trackIdx, track]);

  // Sorted AFTER filtering, rows and ids together, so the selection's positions and ids
  // stay paired.
  const { filtered, filteredIds } = useMemo(() => {
    const order = sortable ? sortPositions(frame, narrowed, sort) : null;
    if (!order) return { filtered: narrowed, filteredIds: narrowedIds };
    return { filtered: order.map((p) => narrowed[p]), filteredIds: order.map((p) => narrowedIds[p]) };
  }, [narrowed, narrowedIds, sort, sortable, frame]);

  useEffect(() => { setPage(0); }, [cf.colFilters, text, search.searchCol, search.exact, track, sort]);

  const gkIdx = groupKeyCol ? frame.columns.indexOf(groupKeyCol) : -1;

  const pageRows = filtered.slice(page * pageSize, (page + 1) * pageSize);
  const nPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const capped = frame.total_rows > frame.rows.length;

  // Excel-style cell selection. Row coordinates are positions in `filtered` (not the
  // page slice), so a whole-column selection aggregates every filtered row rather than
  // just the visible page, and a rectangle survives paging back and forth.
  const colIdx = useMemo(() => vis.map((c) => c.index), [vis]);
  const colLabels = useMemo(() => vis.map((c) => c.label), [vis]);
  const sel = useGridSelection(filtered, colIdx, colLabels, filteredIds);
  const cw = useColumnWidths(
    useMemo(() => [...vis.map((c) => c.key), ...(rowAction ? ["__action"] : [])], [vis, rowAction]),
    frame);
  // Autofit reads every filtered row, not just this page.
  const colTexts = (c: (typeof vis)[number]) => () =>
    filtered.map((r) => cellText(r[c.index], grouped ? rawKeyOf(c.key) : c.key));

  return (
    <div>
      <SearchPanel search={search} matchingRows={filtered.length}
        tools={<>
          {cf.nActive > 0 && (
            <button className="btn btn-sm" onClick={cf.clearAll}>
              Clear {cf.nActive} filter{cf.nActive === 1 ? "" : "s"}
            </button>
          )}
          <span className="muted">
            {filtered.length.toLocaleString()} of {frame.total_rows.toLocaleString()} rows
            {capped && " (showing first " + frame.rows.length.toLocaleString() + " — dashboard totals still use every row)"}
          </span>
        </>} />
      <TrackChips tracks={tracks} counts={trackCounts} track={track} onTrack={setTrack} />
      <div {...sel.gridProps}>
      <div
        ref={(el) => { frozen.scrollRef.current = el; sel.scrollHostRef.current = el; bandLabels.register(el); }}
        className="grid-scroll"
        style={{ maxHeight: scrollMax ?? 480 }}>
        <table
          className={"data-table"
            + (pinnable && pins.pinned.length ? " data-table--separate" : "")
            + (grouped ? " data-table--grouped" : "") + cw.className}
          style={grouped ? ({ "--group-h": `${groupH}px`, ...cw.style } as React.CSSProperties) : cw.style}>
          {cw.colgroup}
          <thead>
            {grouped && (
              <tr ref={bannerRef}>
                {spans.map((sp) => {
                  // A run whose every column is frozen must freeze its banner too,
                  // at the run's own left offset — otherwise the label scrolls out
                  // from over the columns it names and renders clipped.
                  const left = sp.keys.every((k) => frozen.offsetFor(k) !== undefined)
                    ? frozen.offsetFor(sp.firstKey)
                    : undefined;
                  return (
                    <th key={sp.firstKey} colSpan={sp.span}
                      className={`side-band side-band--${sp.side}`
                        + (left !== undefined ? " side-band--pinned" : "")}
                      style={{ left }}>
                      <span className="band-label" {...{ [BAND_LABEL_ATTR]: "" }}
                        data-band-pinned={left !== undefined ? "1" : undefined}>
                        {sp.label}
                      </span>
                    </th>
                  );
                })}
                {rowAction && <th className="side-band side-band--meta" />}
              </tr>
            )}
            <tr>
              {vis.map((c, ci) => {
                const left = frozen.offsetFor(c.key);
                const cls = [
                  left !== undefined ? "pinned-col" + (c.key === lastFrozen ? " pinned-edge" : "") : "",
                  sel.isColSelected(ci) ? "col-selected" : "",
                  grouped ? "col-head" : "",
                  seamKeys.has(c.key) ? "side-start-ins" : "",
                ].filter(Boolean).join(" ");
                return (
                  <th key={c.key} data-col-key={c.key} className={`nowrap ${cls}`}
                    style={{ left }}>
                    <span className="th-inner">
                      <span className="th-label" title="Click to total this column · Ctrl+click to add another"
                        onClick={(e) => { sel.onHeaderClick(ci, e); sel.focusGrid(); }}>
                        {c.label}
                      </span>
                      <HeadControls frame={frame} col={c}
                        filter={{
                          active: (cf.colFilters[c.key]?.length ?? 0) > 0,
                          open: openCol === c.key,
                          options: () => cf.distinctFor(c),
                          selected: cf.colFilters[c.key] ?? [],
                          onToggleOpen: () => setOpenCol(openCol === c.key ? null : c.key),
                          onChange: (v) => cf.setColFilter(c.key, v),
                        }}
                        sort={sortable ? { spec: sort, onSort: setSort } : undefined}
                        pin={pinnable ? { pinned: pins.isPinned(c.key), onToggle: () => pins.toggle(c.key) } : undefined} />
                    </span>
                    {cw.grip(c.key, colTexts(c))}
                  </th>
                );
              })}
              {rowAction && (
                <th className="sticky-note-col col-action">{rowAction.header}{cw.grip("__action")}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, ri) => {
              const rowPos = page * pageSize + ri;  // position in `filtered`, the selection's coordinate space
              // Block banding, from the RENDERED neighbours: a filter or a page boundary
              // that splits a group correctly restarts the rail rather than bracketing
              // rows the reader cannot see.
              const gk = gkIdx >= 0 ? String(r[gkIdx] ?? "") : "";
              const prevGk = gkIdx >= 0 && ri > 0 ? String(pageRows[ri - 1][gkIdx] ?? "") : "";
              const nextGk = gkIdx >= 0 && ri + 1 < pageRows.length
                ? String(pageRows[ri + 1][gkIdx] ?? "") : "";
              const inBlock = gk !== "" && (gk === prevGk || gk === nextGk);
              const rowCls = inBlock
                ? "mgrp-row" + (gk === prevGk ? "" : " mgrp-row-start")
                : undefined;
              return (
              <tr key={rowPos} className={rowCls}>
                {vis.map((c, ci) => {
                  const v = r[c.index];
                  const left = frozen.offsetFor(c.key);
                  const evi = matchEvidenceCellClass(c.key, v);
                  const cls = [
                    "cell-clip",
                    typeof v === "number" ? "num" : "",
                    left !== undefined ? "pinned-col" : "",
                    left !== undefined && c.key === lastFrozen ? "pinned-edge" : "",
                    sel.isSelected(rowPos, ci) ? "cell-selected" : "",
                    grouped && sideOf(c.key) === "ins" ? "side-ins" : "",
                    seamKeys.has(c.key) ? "side-start-ins" : "",
                    matchGroupCellClass(c.key, v),
                    evi ? "mevi-td" : "",
                  ].filter(Boolean).join(" ");
                  const body = cellText(v, grouped ? rawKeyOf(c.key) : c.key);
                  return (
                    <td key={c.key} className={cls} style={{ left }} title={body}
                      data-row={rowPos} data-col={ci}
                      onMouseDown={(e) => { sel.onCellMouseDown(rowPos, ci, e); sel.focusGrid(); }}
                      onMouseEnter={() => sel.onCellMouseEnter(rowPos, ci)}>
                      {/* The chip is an INLINE span, never the cell itself: a td
                          stretches to the row height, so a pill radius on it renders
                          as a full-height ellipse rather than a tag. */}
                      {evi ? <span className={evi}>{body}</span> : body}
                    </td>
                  );
                })}
                {rowAction && (
                  <td className="sticky-note-col cell-center">{rowAction.render(r)}</td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Filtering to nothing left a bare header over an empty body — the grid owns
          its own search and column filters, so it owns saying so, and offering the
          way back. The section-level empty state upstream never fires for these. */}
      {filtered.length === 0 && (
        <EmptyState
          title="No rows match the current filters"
          body={text.trim()
            ? "Nothing in this table matches what you searched or pasted."
            : cf.nActive > 0 ? "Every row is filtered out by the column filters above."
            : "No row is in the track chosen above."}
          action={{
            label: text.trim() ? "Clear search"
              : cf.nActive > 0 ? `Clear ${cf.nActive} filter${cf.nActive === 1 ? "" : "s"}`
              : "Show all rows",
            onClick: () => {
              if (text.trim()) setText("");
              else if (cf.nActive > 0) cf.clearAll();
              else setTrack(null);
            },
          }}
        />
      )}
      {sel.statusBar}
      </div>
      {filtered.length > 0 && (
        <div className="grid-pager">
          <button className="btn" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Prev</button>
          <span className="muted">Page {page + 1} of {nPages || 1}</span>
          <button className="btn" onClick={() => setPage((p) => Math.min(nPages - 1, p + 1))} disabled={page >= nPages - 1}>Next</button>
        </div>
      )}
    </div>
  );
}
